// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ImageType } from 'model/Image';
import Ellipsis from 'components/treactui-template/atoms/Ellipsis';
import { useAppDispatch } from 'store/hooks';
import { changeSelectedImage } from 'store/reducers/projectImageSlice';
import { AnimationCss } from 'components/treactui-template/atoms/animation/AnimatePulse';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { loadArrayIntoImageElement } from 'components/treactui-template/meta/loadImageIntoElement';
import { useAuthContext } from '@tls/auth';
import DatasetTag from 'components/common/DatasetTag';
import { DatasetFilterType, QCResult } from 'components/project/data/filter/filters-defaults';
import ProcessTag from 'components/common/ProcessTag';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import { UseCase } from '@tls/sw91-communication/types/com.base';
import IconLabel from 'components/treactui-template/icons/IconLabel';
import { Colors, Table } from '@tls/treact-ui';
import { IconCircle } from '@tls/treact-ui/icons';

export type Props = {
  entry: ImageType;
  index: number;
  selected?: boolean;
};

const ImageTableEntry = React.forwardRef<HTMLDivElement, Props>(function ImageTableEntry(
  { entry, index, selected }: Props,
  ref
) {
  const dispatch = useAppDispatch();
  const className: string = selected ? 'selected' : '';
  const { id } = useParams();
  const { selectedProject } = useProjectsMetadata(id);
  const isQC = useMemo(
    () => selectedProject?.projectUseCase?.useCase === UseCase.QUALITY_CHECK,
    [selectedProject?.projectUseCase?.useCase]
  );

  const onClick = useCallback(() => dispatch(changeSelectedImage(entry.imageMd5)), [dispatch, entry.imageMd5]);

  const imageElement = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (!id || !entry.previewData) return;

    loadArrayIntoImageElement([entry.previewData as Uint8Array<any>], 'image/png', imageElement);
  }, [id, entry.imageMd5, entry.previewData]);

  const { appBaseUrl } = useAuthContext();

  const labeledIndicator = useMemo(() => {
    if (entry?.hasMask && isQC) {
      return (
        <DatasetTag
          dataset={entry.isValidation ? DatasetFilterType.EvaluationSet : DatasetFilterType.TrainingSet}
          size={'100%'}
        />
      );
    } else if (entry?.hasMask) {
      return <IconLabel width={'75%'} />;
    } else {
      return <IconCircle strokeWidth='2px' viewBox='-2 -2 40 40' width={'75%'} />;
    }
  }, [entry, isQC]);

  return (
    <Table.Row
      ref={ref}
      index={index}
      className={className}
      onClick={onClick}
      data-testid={`label-image-${entry.imageMd5}-row`}
    >
      <Table.Cell width={'15%'} noIndent>
        <Image
          src={`${appBaseUrl}/images/image-placeholder.png`}
          ref={imageElement}
          data-testid={`label-image-${entry.imageMd5}-preview`}
        />
      </Table.Cell>
      {isQC && (
        <Table.Cell width={'12%'} alignment='CENTER' noIndent data-testid={`label-image-${entry.imageMd5}-qc-result`}>
          {entry.hasMask && <ProcessTag processTag={entry?.qcResult ?? QCResult.Undefined} size='100%' />}
        </Table.Cell>
      )}
      <Table.Cell width={'12%'} alignment='CENTER' noIndent data-testid={`label-image-${entry.imageMd5}-label-status`}>
        {labeledIndicator}
      </Table.Cell>
      <Table.Cell width={1} noIndent data-testid={`label-image-${entry.imageMd5}-name`}>
        <Ellipsis>{entry.imageName}</Ellipsis>
      </Table.Cell>
      <Table.Cell width={'10%'} noIndent data-testid={`label-image-${entry.imageMd5}-label-order`}>
        {entry.labelOrder ?? '-'}
      </Table.Cell>
    </Table.Row>
  );
});

export default ImageTableEntry;

const Image = styled.img`
  width: 2.5rem;
  margin: 0.25rem;
  background-color: rgba(${Colors.trgrey2.rgb}, 0.4);
  ${AnimationCss}
`;
